/**
 * Phase 11 — handler for `ide.toast.show`.
 *
 * Shows a transient toast in the LexIDE UI. The action originates as
 * an LLM tool_use call inside the lexruntime container; OpenClaw core
 * auto-emits a `session.tool` EventFrame which `usePlatformOpListener`
 * picks up and dispatches here.
 *
 * Catalog `kind` enum (`info | warning | error`) maps to the toast
 * store's kind enum (`info | success | warn | error`). The mapping is
 * a 1-line adapter — toast store uses "warn", catalog uses "warning"
 * (matches the more conventional English form).
 */
import { useToastStore } from "@/stores/useToastStore";
import type { Handler } from "@/lib/platform-ops-registry";

interface ToastShowParams {
  message: string;
  kind?: "info" | "warning" | "error";
}

export const ideToastShowHandler: Handler = (raw, _ctx) => {
  const params = raw as Partial<ToastShowParams>;
  if (typeof params.message !== "string" || params.message.length === 0) {
    return {
      ok: false,
      error: {
        code: "IDE_INVALID_PARAMS",
        message: "ide.toast.show requires a non-empty `message` string.",
      },
    };
  }

  const catalogKind = params.kind ?? "info";
  // Catalog → toast-store kind mapping. "warning" → "warn" is the
  // only non-identity case.
  const toastKind: "info" | "warn" | "error" =
    catalogKind === "warning" ? "warn" : catalogKind;

  const id = useToastStore.getState().addToast({
    text: params.message,
    kind: toastKind,
    autoDismissMs: 5000,
  });

  return { ok: true, data: { toastId: id, kind: toastKind } };
};
